import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog } from '@betareader/design-system';
import styled from 'styled-components';
import { FREE_TASTE } from 'src/utils/critiqueGroupUtil';
import Icon from '../AppIcon';

/**
 * The free-taste upgrade prompts (design A2 banner + B2 modal).
 *
 * Premium is presented as an upgrade pipe, not a wall: the free taste is real
 * (1 group / 5 members / 1 book) and the prompt only appears once a limit is
 * actually hit, explaining what more would buy.
 */

const Banner = styled.div`
  margin-top: 18px;
  background: var(--br-warning-surface);
  border: 1px solid var(--br-warning-border);
  border-radius: 12px;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const BannerText = styled.span`
  flex: 1;
  font-size: 13.5px;
  color: var(--br-warning-text);
  /* The 220px floor is only there to keep the text and the CTA on one line on
     roomy screens; min() lets it collapse rather than overflow a narrow phone. */
  min-width: min(220px, 100%);
`;

const Hero = styled.div`
  height: 96px;
  background: linear-gradient(135deg, #f2954a, #e06a12);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const HeroIcon = styled.div`
  width: 58px;
  height: 58px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 28px;
`;

const Perk = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13.5px;
`;

export const FreeTasteBanner: React.FC<{ onUpgrade: () => void }> = ({
  onUpgrade,
}) => {
  const { t } = useTranslation();
  return (
    <Banner data-testid="free-taste-banner">
      <Icon name="gem" style={{ color: 'var(--br-warning-text)', fontSize: 18 }} />
      <BannerText>
        <strong>{t('CritiqueFreeTaste', 'You’re on the free taste.')}</strong>{' '}
        {t(
          'CritiqueFreeTasteBody',
          'Upgrade to Premium to host more groups, add more books & invite more readers.'
        )}
      </BannerText>
      <Button onClick={onUpgrade}>
        {t('Upgrade', 'Upgrade')}
      </Button>
    </Banner>
  );
};

export type TierLimit = 'groups' | 'books' | 'members' | 'public';

type GateProps = {
  open: boolean;
  onClose: () => void;
  onUpgrade: () => void;
  /** Which limit was hit — drives the headline. */
  limit?: TierLimit;
  /** The server's message, when it sent one (it is the source of truth). */
  serverMessage?: string;
};

export const TierGateModal: React.FC<GateProps> = ({
  open,
  onClose,
  onUpgrade,
  limit = 'groups',
  serverMessage,
}) => {
  const { t } = useTranslation();

  const headline = () => {
    switch (limit) {
      case 'books':
        return t('CritiqueGateBooks', 'Add more books with Premium');
      case 'members':
        return t('CritiqueGateMembers', 'Invite more readers with Premium');
      case 'public':
        return t('CritiqueGatePublic', 'Let writers find your group with Premium');
      default:
        return t('CritiqueGateGroups', 'You’ve reached your free limit');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={headline()}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('MaybeLater', 'Maybe later')}
          </Button>
          <Button onClick={onUpgrade} data-testid="tier-gate-upgrade">
            {t('UpgradeToPremium', 'Upgrade to Premium')}
          </Button>
        </>
      }
    >
      <Hero>
        <HeroIcon>
          <Icon name="gem" style={{ margin: 0 }} />
        </HeroIcon>
      </Hero>
      <p style={{ margin: '12px 0', color: 'var(--br-muted)', fontSize: 14.5, lineHeight: 1.55 }}>
        {serverMessage ||
          t(
            'CritiqueGateBody',
            'Your free taste includes {{groups}} group, up to {{members}} members, and {{books}} book. Upgrade to Premium to grow your writing family.',
            {
              groups: FREE_TASTE.maxGroups,
              members: FREE_TASTE.memberCap,
              books: FREE_TASTE.maxBooks,
            }
          )}
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginTop: 6 }}>
        <Perk>
          <Icon name="check" style={{ color: 'var(--br-success-text)' }} />
          {t('CritiquePerkGroups', 'Host unlimited critique groups')}
        </Perk>
        <Perk>
          <Icon name="check" style={{ color: 'var(--br-success-text)' }} />
          {t('CritiquePerkBooks', 'Add multiple books per group')}
        </Perk>
        <Perk>
          <Icon name="check" style={{ color: 'var(--br-success-text)' }} />
          {t('CritiquePerkMembers', 'Invite as many readers as you like')}
        </Perk>
        <Perk>
          <Icon name="check" style={{ color: 'var(--br-success-text)' }} />
          {t('CritiquePerkPublic', 'List your group so writers can find it')}
        </Perk>
      </div>
    </Dialog>
  );
};
